import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/**
 * Cart upsell slider (rendered by `snippets/cart-upsell.liquid`).
 *
 * Once visible, fetches `sections/cart-upsell-recommendations.liquid` through the Product
 * Recommendations API: complementary first (Search & Discovery), then related (which falls back
 * to the fallback collection on the server).
 *
 * The cart re-render (morph) skips the results wrapper (`data-skip-subtree-update`) but updates
 * `data-product-id` / `data-cart-key` on this element, which triggers a reload.
 *
 * Adding to cart: "+" adds single-variant products directly, or opens the card's option panel
 * (sizes rendered with the card, so it opens instantly). The add uses the same request and
 * `CartAddEvent` as Horizon's product form, so the drawer, /cart and progress bar update as usual.
 *
 * @typedef {{ results: HTMLElement, status: HTMLElement }} CartUpsellRefs
 * @extends {Component<CartUpsellRefs>}
 */
class CartUpsell extends Component {
  requiredRefs = ['results', 'status'];

  /**
   * Fetched section HTML, keyed by request URL + cart contents (the server filters by cart)
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Key (product + cart contents) of the results currently shown */
  #renderedKey = '';

  #hasBeenVisible = false;

  #intersectionObserver = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      this.#intersectionObserver.disconnect();
      this.#hasBeenVisible = true;
      this.#load();
    },
    { rootMargin: '0px 0px 200px 0px' }
  );

  #attributeObserver = new MutationObserver(() => {
    if (this.#hasBeenVisible) this.#load();
  });

  connectedCallback() {
    super.connectedCallback();

    this.#intersectionObserver.observe(this);
    this.#attributeObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-cart-key'],
    });

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    this.addEventListener('mousedown', this.#handleMouseDown, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('mousedown', this.#handleMouseDown, { capture: true });
    this.#intersectionObserver.disconnect();
    this.#attributeObserver.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Loads recommendations for the current source product, unless they're already shown.
   */
  async #load() {
    const { productId, cartKey = '' } = this.dataset;
    if (!productId) return;

    const key = `${productId}|${cartKey}`;
    if (key === this.#renderedKey) return;

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      let results = await this.#fetchResults('complementary', controller.signal);

      if (this.#countOf(results) === 0) {
        results = await this.#fetchResults('related', controller.signal);
      }

      this.#render(results, key);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      // Hide quietly; the upsell is optional and must never break the cart
      this.#render(null, key);
      console.warn('[cart-upsell] Could not load recommendations', error);
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  /**
   * Fetches the recommendations section for one intent and returns its results element.
   * @param {'complementary' | 'related'} intent
   * @param {AbortSignal} signal
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchResults(intent, signal) {
    const { url, sectionId, productId, cartKey = '', limit } = this.dataset;
    const requestUrl = `${url}?product_id=${productId}&intent=${intent}&limit=${limit}&section_id=${sectionId}`;
    const cacheKey = `${requestUrl}|${cartKey}`;

    let html = this.#cache.get(cacheKey);

    if (!html) {
      const response = await fetch(requestUrl, { signal });
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      html = await response.text();
      this.#cache.set(cacheKey, html);
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return parsed.querySelector('.cart-upsell-results');
  }

  /**
   * @param {HTMLElement | null} results
   * @returns {number}
   */
  #countOf(results) {
    return Number(results?.dataset.count ?? 0);
  }

  /**
   * Swaps in the new results. Old cards stay on screen until this runs, so there's no empty flash.
   * @param {HTMLElement | null} results
   * @param {string} key
   */
  #render(results, key) {
    this.#renderedKey = key;

    if (results && this.#countOf(results) > 0) {
      this.refs.results.replaceChildren(results);
    } else {
      this.refs.results.replaceChildren();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-upsell-toggle]');
    if (toggle) {
      const card = toggle.closest('.cart-upsell-card');
      const panel = card?.querySelector('.cart-upsell-card__options');
      if (panel instanceof HTMLElement) this.#setPanelOpen(panel, panel.hidden);
      return;
    }

    const addButton = event.target.closest('[data-upsell-add]');
    if (addButton instanceof HTMLButtonElement && !addButton.disabled) {
      this.#addToCart(addButton);
    }
  };

  /**
   * The slider starts a mouse drag on any mousedown inside it. If the mouse moves even 1px, it
   * captures the pointer, and the click lands on the slider instead of our button. Presses on
   * "+" and inside the option panel never reach the slider.
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.cart-upsell-card__add, .cart-upsell-card__options')) {
      event.stopPropagation();
    }
  };

  /**
   * Escape closes an open option panel without closing the cart drawer.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !(event.target instanceof Element)) return;

    const panel = event.target.closest('.cart-upsell-card__options');
    if (!(panel instanceof HTMLElement) || panel.hidden) return;

    event.preventDefault();
    event.stopPropagation();
    this.#setPanelOpen(panel, false);
  };

  /**
   * Opens or closes a card's option panel. Only one panel is open at a time.
   * @param {HTMLElement} panel
   * @param {boolean} open
   */
  #setPanelOpen(panel, open) {
    if (open) {
      for (const openPanel of this.querySelectorAll('.cart-upsell-card__options:not([hidden])')) {
        if (openPanel instanceof HTMLElement && openPanel !== panel) this.#setPanelOpen(openPanel, false);
      }
    }

    const card = panel.closest('.cart-upsell-card');
    const plusButton = card?.querySelector('.cart-upsell-card__add');

    panel.hidden = !open;
    plusButton?.setAttribute('aria-expanded', String(open));

    if (open) {
      const firstOption = panel.querySelector('.cart-upsell-card__option:not([disabled])');
      if (firstOption instanceof HTMLElement) firstOption.focus();
    } else if (plusButton instanceof HTMLElement) {
      plusButton.focus();
    }
  }

  /**
   * Adds one variant to the cart, using the same request and event as Horizon's product form.
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    const card = button.closest('.cart-upsell-card');
    const variantId = button.dataset.variantId;
    if (!(card instanceof HTMLElement) || !variantId) return;

    const errorMessage = card.querySelector('.cart-upsell-card__error');
    if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;

    card.setAttribute('aria-busy', 'true');
    card.classList.add('cart-upsell-card--adding');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    // Ask for the cart sections on this page so they can re-render without another request
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) throw new Error(data.description || data.message);

      const title = card.querySelector('.cart-upsell-card__title')?.textContent?.trim() ?? '';
      this.refs.status.textContent = (this.dataset.addedLabel ?? '').replace('[title]', title);

      // Re-renders the drawer / cart page; the new cart key then reloads this upsell
      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-upsell',
          itemCount: 1,
          productId: card.dataset.productId,
          variantId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.warn('[cart-upsell] Could not add to cart', error);

      if (errorMessage instanceof HTMLElement) {
        errorMessage.textContent =
          error instanceof Error && error.message ? error.message : this.dataset.errorLabel ?? '';
        errorMessage.hidden = false;
      }
    } finally {
      card.removeAttribute('aria-busy');
      card.classList.remove('cart-upsell-card--adding');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
